import { createFileRoute, redirect } from '@tanstack/react-router';
import { getActiveOrgContext, setActiveOrgId } from '@/api/axios-instance';
import {
  resolveOrgIdentifier,
  isShortCode,
  isClerkOrgId,
  isOrgNotFoundError,
  shortCodeOrgHref,
} from '@/lib/org-utils';
import { clearPendingAgencySwitch } from '@/features/agency/agency-pending-switch-store';
import { OrgLayout } from '@/components/layout/org-route-layout';

export const Route = createFileRoute('/_auth/$orgId')({
  beforeLoad: async ({ params, location, preload }) => {
    const { orgId: identifier } = params;

    // Validate: must be numeric shortCode or org_xxx
    if (!isShortCode(identifier) && !isClerkOrgId(identifier)) {
      throw redirect({ to: '/login' });
    }

    // If it's a Clerk org ID, redirect to the same page under the shortCode URL
    if (isClerkOrgId(identifier)) {
      try {
        const { shortCode } = await resolveOrgIdentifier(identifier);
        throw redirect({ href: shortCodeOrgHref(location, identifier, shortCode), replace: true });
      } catch (e) {
        if (e instanceof Response || (e && typeof e === 'object' && 'to' in e)) throw e;
        clearPendingAgencySwitch();
        throw redirect({
          to: '/login',
          search: { orgResolutionFailed: isOrgNotFoundError(e) ? 'not-found' : 'network' },
        });
      }
    }

    // It's a shortCode - resolve to get Clerk org ID
    try {
      const { orgId: clerkOrgId } = await resolveOrgIdentifier(identifier);
      // A hover preload (e.g. a salon card on the agency page) must not retarget or cancel the current page's calls.
      if (!preload && getActiveOrgContext().orgId !== clerkOrgId) {
        setActiveOrgId(clerkOrgId, { tokenStrategy: 'session' });
      }
      return { clerkOrgId, shortCode: identifier };
    } catch (e) {
      console.error('Failed to resolve org shortCode:', identifier, e);
      clearPendingAgencySwitch(identifier);
      throw redirect({
        to: '/login',
        search: { orgResolutionFailed: isOrgNotFoundError(e) ? 'not-found' : 'network' },
      });
    }
  },
  // A slow salon switch keeps the current salon on screen (OrgLayout then shows the switching frame) instead of
  // swapping to the boot loader after the default pending delay. A cold open still shows the pending loader.
  pendingMs: Infinity,
  component: OrgLayout,
});
